import React, { useState, useEffect, useMemo } from 'react';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getAllTickets, updateTicket } from '../../../api/tickets';
import './BetaBoardView.scss';

const BetaBoardView = ({ onNavigate }) => {
  const { refreshMyTickets, statusOptions } = useBetaData();
  const { usersMapByEmail } = useBetaData();
  const { viewingCompanyId } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedTicket, setDraggedTicket] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  useEffect(() => {
    loadTickets();
  }, [viewingCompanyId]);

  const loadTickets = async () => {
    setLoading(true);
    const { data } = await getAllTickets({ companyId: viewingCompanyId });
    setTickets(data || []);
    setLoading(false);
  };

  // 補上 tickets 中有但 statusOptions 中沒有的狀態作為額外 column
  const effectiveStatusOptions = useMemo(() => {
    const opts = (statusOptions || []).filter((s) => s.value !== 'closed');
    const knownValues = new Set(opts.map((s) => s.value));
    const extras = [];
    tickets.forEach((t) => {
      const s = t.status || 'open';
      if (s !== 'closed' && !knownValues.has(s)) {
        knownValues.add(s);
        extras.push({ value: s, label: s, color: '#94a3b8' });
      }
    });
    return extras.length ? [...opts, ...extras] : opts;
  }, [statusOptions, tickets]);

  const groupedByStatus = useMemo(() => {
    const groups = {};
    effectiveStatusOptions.forEach((col) => {
      groups[col.value] = [];
    });
    tickets.forEach((t) => {
      const status = t.status || 'open';
      if (groups[status]) {
        groups[status].push(t);
      } else {
        const firstKey = effectiveStatusOptions[0]?.value || 'open';
        if (!groups[firstKey]) groups[firstKey] = [];
        groups[firstKey].push(t);
      }
    });
    return groups;
  }, [tickets, effectiveStatusOptions]);

  const handleDragStart = (e, ticket) => {
    setDraggedTicket(ticket);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnKey);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    if (!draggedTicket || draggedTicket.status === newStatus) {
      setDraggedTicket(null);
      return;
    }

    const oldStatus = draggedTicket.status;
    // Optimistic update
    setTickets((prev) =>
      prev.map((t) =>
        t.id === draggedTicket.id ? { ...t, status: newStatus } : t
      )
    );

    const { error } = await updateTicket(draggedTicket.id, { status: newStatus });
    if (error) {
      // Rollback
      setTickets((prev) =>
        prev.map((t) =>
          t.id === draggedTicket.id ? { ...t, status: oldStatus } : t
        )
      );
    } else {
      refreshMyTickets?.();
    }
    setDraggedTicket(null);
  };

  if (loading) {
    return (
      <div className="beta-board-view">
        <div className="board-loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="beta-board-view">
      <div className="board-columns">
        {effectiveStatusOptions.map((col) => {
          const columnTickets = groupedByStatus[col.value] || [];
          return (
            <div
              key={col.value}
              className={`board-column ${dragOverColumn === col.value ? 'drag-over' : ''}`}
              onDragOver={(e) => handleDragOver(e, col.value)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, col.value)}
            >
              <div className="column-header">
                <span className="column-dot" style={{ backgroundColor: col.color }} />
                <span className="column-title">{col.label}</span>
                <span className="column-count">{columnTickets.length}</span>
              </div>
              <div className="column-body">
                {columnTickets.length === 0 ? (
                  <div className="column-empty">暫無任務</div>
                ) : (
                  columnTickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className={`board-card ${draggedTicket?.id === ticket.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, ticket)}
                      onClick={() => onNavigate?.('ticket', ticket.project_id, ticket.id, { ticketNo: ticket.ticket_no })}
                    >
                      <div className="card-header">
                        <span className="card-id">#{ticket.ticket_no}</span>
                        {ticket.priority > 1 && (
                          <span className={`card-priority p${ticket.priority}`}>
                            {ticket.priority === 3 ? '高' : ticket.priority === 2 ? '中' : ''}
                          </span>
                        )}
                      </div>
                      <div className="card-title">{ticket.title}</div>
                      <div className="card-footer">
                        <div className="card-assignee-name">
                          {(() => {
                            const first = (ticket.ticket_assignees || [])[0];
                            if (!first) return null;
                            const email = first.profiles?.email?.toLowerCase();
                            const name = email ? usersMapByEmail[email]?.name : null;
                            return name || first.profiles?.email || null;
                          })()}
                        </div>
                        {ticket.end_date && (
                          <span className="card-date">{ticket.end_date}</span>
                        )}
                      </div>
                      {/* 專案標籤 */}
                      {ticket.projects?.name && (
                        <div className="card-project">
                          <span
                            className="project-dot-sm"
                            style={{ backgroundColor: ticket.projects?.color || '#667eea' }}
                          />
                          {ticket.projects.name}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BetaBoardView;
